/**
 * @file Formules d'equilibrage pures et testees (couts d'upgrade, PV/argent de stage, rebirth, etoiles, critiques). Reference : design/BALANCING.md.
 */
import { Decimal } from '@shared/bignum';
import type { BiomeId, UpgradeId } from '@shared/enums';
import type { BalanceConfig, UpgradeSpec } from '@config/types';

// =================================================================================================
// Upgrades Power / Speed / Money
// =================================================================================================

/** Multiplicateur apporte par un upgrade au niveau `level` (niveau 1 = x1). */
export const upgradeMultiplier = (spec: UpgradeSpec, level: number): Decimal =>
  spec.kind === 'mult' ? Decimal.pow(spec.perLevel, level - 1) : new Decimal(1 + spec.perLevel * (level - 1));

/**
 * Cout pour passer de `level` a `level + 1`.
 * cost(L) = baseCost * g^(L-1)                         si L <= softCap
 *         = baseCost * g^(L-1) * m^(L - softCap)       sinon (m = costGrowthBeyondMult = 2)
 */
export const upgradeCost = (bal: BalanceConfig, id: UpgradeId, level: number): Decimal => {
  const u = bal.upgrades;
  const spec = u.specs[id];
  let c = Decimal.pow(u.costGrowth, level - 1).times(spec.baseCost);
  if (level > u.softCapLevel) c = c.times(Decimal.pow(u.costGrowthBeyondMult, level - u.softCapLevel));
  return c;
};

/** Cout total de `count` achats a partir de `level` (series geometriques par morceaux). */
export const upgradeCostRange = (bal: BalanceConfig, id: UpgradeId, level: number, count: number): Decimal => {
  const u = bal.upgrades;
  const n = Math.max(0, Math.min(count, u.maxLevel - level));
  if (n === 0) return new Decimal(0);
  const first = upgradeCost(bal, id, level);
  const soft = u.softCapLevel;
  // Partie 1 : niveaux level..min(level+n-1, soft) a raison g.
  const n1 = Math.max(0, Math.min(n, soft - level + 1));
  let total = new Decimal(0);
  if (n1 > 0) total = total.plus(geometricSum(first, u.costGrowth, n1));
  const n2 = n - n1;
  if (n2 > 0) {
    const start2 = upgradeCost(bal, id, level + n1);
    total = total.plus(geometricSum(start2, u.costGrowth * u.costGrowthBeyondMult, n2));
  }
  return total;
};

/** Nombre maximal d'achats abordables avec `budget` a partir de `level` (borne par maxLevel). */
export const upgradeMaxAffordable = (bal: BalanceConfig, id: UpgradeId, level: number, budget: Decimal): number => {
  const u = bal.upgrades;
  const remainingLevels = u.maxLevel - level;
  if (remainingLevels <= 0 || budget.lt(upgradeCost(bal, id, level))) return 0;
  const soft = u.softCapLevel;
  let count = 0;
  let money = budget;
  let lv = level;
  if (lv <= soft) {
    const cap = Math.min(soft - lv + 1, remainingLevels);
    const k = Math.min(cap, Math.floor(Decimal.affordGeometricSeries(money, upgradeCost(bal, id, lv), u.costGrowth, 0).toNumber()));
    money = money.sub(upgradeCostRange(bal, id, lv, k));
    count += k;
    lv += k;
    if (k < cap) return count;
  }
  const left = u.maxLevel - lv;
  if (left > 0) {
    const r = u.costGrowth * u.costGrowthBeyondMult;
    const k = Math.min(left, Math.floor(Decimal.affordGeometricSeries(money, upgradeCost(bal, id, lv), r, 0).toNumber()));
    count += Math.max(0, k);
  }
  return count;
};

const geometricSum = (first: Decimal, ratio: number, n: number): Decimal =>
  ratio === 1 ? first.times(n) : first.times(Decimal.pow(ratio, n).sub(1)).div(ratio - 1);

// =================================================================================================
// Stages (mode Main)
// =================================================================================================

export interface StageDisplay {
  /** Numero de stage/monde (1-based). */
  stage: number;
  /** Niveau dans le stage (1-based). */
  level: number;
}

export const stageValueToDisplay = (bal: BalanceConfig, value: number): StageDisplay => ({
  stage: Math.floor(value / bal.stage.levelsPerStage) + 1,
  level: (value % bal.stage.levelsPerStage) + 1,
});

export const displayToStageValue = (bal: BalanceConfig, d: StageDisplay): number => (d.stage - 1) * bal.stage.levelsPerStage + (d.level - 1);

/** Le dernier niveau de chaque stage (niveau 10) est un niveau boss. */
export const isBossLevel = (bal: BalanceConfig, value: number): boolean => value % bal.stage.levelsPerStage === bal.stage.levelsPerStage - 1;

/** log10 des PV d'un arbre normal au niveau `value` (bandes de croissance cumulees, O(nb bandes)). */
export const stageHpLog10 = (bal: BalanceConfig, value: number): number => {
  let log = Math.log10(bal.stage.baseTreeHp);
  let remaining = Math.max(0, value);
  let prev = 0;
  for (const band of bal.stage.hpGrowthBands) {
    if (remaining <= 0) break;
    const span = Math.min(remaining, band.untilValue - prev);
    log += span * Math.log10(band.growth);
    remaining -= span;
    prev = band.untilValue;
  }
  return log;
};

/** PV d'un arbre normal (avant TargetDef.hpMult). */
export const stageHp = (bal: BalanceConfig, value: number): Decimal => Decimal.pow(10, stageHpLog10(bal, value));

/** Argent d'un arbre normal (avant rewardMult et stat `money`). */
export const stageMoney = (bal: BalanceConfig, value: number): Decimal => stageHp(bal, value).times(bal.stage.moneyPerHp);

/**
 * Nombre d'arbres du niveau `value` (le tree_boss du niveau 10 s'ajoute en plus) :
 *   n = min(max, round(first + stageGain * (1 - e^(-s / stageScale)) + levelGain * l / (L - 1)))
 *   s = index de stage (0-based), l = index du niveau dans le stage (0..L-1), L = levelsPerStage.
 * Strictement croissant du niveau 1 au niveau 10 d'un stage (pas >= levelGain / (L - 1) >= 1), base croissante d'un stage
 * a l'autre mais bornee (first + stageGain), total plafonne a `max`.
 */
export const levelTreeCount = (bal: BalanceConfig, value: number): number => {
  const c = bal.stage.treeCount;
  const per = bal.stage.levelsPerStage;
  const v = Math.max(0, Math.floor(value));
  const s = Math.floor(v / per);
  const l = v % per;
  const base = c.first + c.stageGain * (1 - Math.exp(-s / Math.max(1e-9, c.stageScale)));
  const n = base + (per > 1 ? (c.levelGain * l) / (per - 1) : 0);
  return Math.max(1, Math.min(c.max, Math.round(n)));
};

export const levelSeconds = (bal: BalanceConfig, value: number): number =>
  isBossLevel(bal, value) ? bal.stage.bossLevelSeconds : bal.stage.levelSeconds;

/** Theme du stage : rotation de BIOME_ORDER, un changement tous les `biomeStageSpan` stages (1 = chaque stage a son theme). */
export const biomeForStageValue = (bal: BalanceConfig, value: number, order: readonly BiomeId[]): BiomeId => {
  const stage = Math.floor(value / bal.stage.levelsPerStage);
  return order[Math.floor(stage / bal.stage.biomeStageSpan) % order.length] as BiomeId;
};

// =================================================================================================
// Rebirth
// =================================================================================================

/** Ruby gagnes : floor(scale * (moneyAccum / minAccum)^exponent), 0 si moneyAccum < minAccum. */
export const rebirthRubyGain = (bal: BalanceConfig, moneyAccum: Decimal): Decimal => {
  const r = bal.rebirth;
  if (moneyAccum.lt(r.rubyMinMoneyAccum)) return new Decimal(0);
  return moneyAccum.div(r.rubyMinMoneyAccum).pow(r.rubyExponent).times(r.rubyScale).floor();
};

/** Cout en Ruby pour passer un bonus de Rebirth du niveau L a L+1 (L commence a 0). */
export const rebirthUpgradeCost = (bal: BalanceConfig, level: number): Decimal => {
  const r = bal.rebirth;
  return new Decimal(Math.ceil(r.costBase + r.costLinear * level + Math.pow(level, r.costPower) / r.costPowerDiv));
};

/** Multiplicateur permanent d'un bonus de Rebirth au niveau L : 1.05^L. */
export const rebirthMultiplier = (bal: BalanceConfig, level: number): Decimal => Decimal.pow(bal.rebirth.bonusPerLevel, level);

// =================================================================================================
// Etoiles
// =================================================================================================

/** Multiplicateur de degats du slot : 1 au niveau 0, sinon multBase * multGrowth^niveau. */
export const starMultiplier = (bal: BalanceConfig, level: number): Decimal =>
  level <= 0 ? new Decimal(1) : Decimal.pow(bal.stars.multGrowth, level).times(bal.stars.multBase);

/**
 * Chance de reussite pour passer de `level` a `level + 1` :
 * p = max(successMin, successBase - successPerLevel * level) ; a partir de pityFromLevel, + pityStep par echec consecutif.
 */
export const starSuccessChance = (bal: BalanceConfig, level: number, consecutiveFails: number): number => {
  const s = bal.stars;
  let p = Math.max(s.successMin, s.successBase - s.successPerLevel * level);
  if (level >= s.pityFromLevel) p += s.pityStep * consecutiveFails;
  return Math.min(1, p);
};

// =================================================================================================
// Combat
// =================================================================================================

/** Esperance du multiplicateur de critique : 1 + p * (critDamage - 1) (utilise par le hors-ligne). */
export const expectedCritFactor = (critChance: number, critDamage: number): number => 1 + Math.min(1, Math.max(0, critChance)) * (critDamage - 1);

/**
 * Cadence effective. L'intervalle ne descend jamais sous un tic (dt) ; le surplus de vitesse est converti en degats.
 * Retourne { interval, damageMult }.
 */
export const effectiveFireRate = (baseInterval: number, attackSpeedMult: number, dt: number, overflowToDamage: boolean): { interval: number; damageMult: number } => {
  const raw = baseInterval / Math.max(1e-9, attackSpeedMult);
  if (raw >= dt) return { interval: raw, damageMult: 1 };
  return { interval: dt, damageMult: overflowToDamage ? dt / raw : 1 };
};
